import { GroupChatMessageViewObject, GroupChatViewObject, HubConnectionViewObject, NotificationViewObject, UserViewObject, CaseFileViewObject, CaseViewObject, EmailFlowViewObject, CasePersonViewObject } from '@/api';
import { defaultReconnectPolicy, SignalRClient } from '@/core/signalr/signalr.client';
import { isDevMode } from '@/core/infrastructure/environment';
import { LogLevel } from '@microsoft/signalr';
import bus from '@/core/bus';

const SIGNALR_URL = import.meta.env.VITE_API_BASE + '/hub';
const SIGNALR_LOGLEVEL = isDevMode() ? LogLevel.Information : LogLevel.Error;

export class ServerHub extends SignalRClient {
    constructor() {
        super(SIGNALR_URL, SIGNALR_LOGLEVEL, defaultReconnectPolicy, false);

        // NOTIFICATIONS
        this.addEvent('NotificationCreated', (notification: NotificationViewObject) => {
            bus.emit('NotificationCreated', notification);
        });
        
        this.addEvent('NotificationUpdated', (notification: NotificationViewObject) => {
            bus.emit('NotificationUpdated', notification);
        });

        this.addEvent('NotificationsUpdated', (notifications: NotificationViewObject[]) => {
            bus.emit('NotificationsUpdated', notifications);
        });
                        
        this.addEvent('NotificationDeleted', (notificationId: string) => {
            bus.emit('NotificationDeleted', notificationId);
        });

        this.addEvent('CaseFileCreated', (file: CaseFileViewObject) => {
            bus.emit('CaseFileCreated', file);
        });

        this.addEvent('CaseFileDeleted', (caseFileId: string) => {
            bus.emit('CaseFileDeleted', caseFileId);
        });

        this.addEvent('CaseUpdated', (caseData: CaseViewObject) => {
            bus.emit('CaseUpdated', caseData);
        });

        this.addEvent('CasePersonUpdated', (data: CasePersonViewObject) => {
            bus.emit('CasePersonUpdated', data);
        });

        this.addEvent('EmailFlowCreated', (flow: EmailFlowViewObject) => {
            bus.emit('EmailFlowCreated', flow);
        });

        this.addEvent('EmailFlowUpdated', (flow: EmailFlowViewObject) => {
            bus.emit('EmailFlowUpdated', flow);
        });

        this.addEvent('EmailFlowDeleted', (emailFlowId: string) => {
            bus.emit('EmailFlowDeleted', emailFlowId);
        });
        
        // CHAT
        this.addEvent('ChatMessageReceived', (message: GroupChatMessageViewObject) => {
            bus.emit('ChatMessageReceived', message);
        });
        
        this.addEvent('GroupChatCreatedOrUpdated', (chat: GroupChatViewObject) => {
            bus.emit('GroupChatCreatedOrUpdated', chat);
        });
        
        this.addEvent('GroupChatRemoved', (chatId: string) => {
            bus.emit('GroupChatRemoved', chatId);
        });

        // USERS
        this.addEvent('UserCreated', (user: UserViewObject) => {
            bus.emit('UserCreated', user);
        });

        this.addEvent('UserUpdated', (user: UserViewObject) => {
            bus.emit('UserUpdated', user);
        });
                                
        this.addEvent('UsersUpdated', (users: UserViewObject[]) => {
            bus.emit('UsersUpdated', users);
        });
                                
        this.addEvent('UserRemoved', (userId: string) => {
            bus.emit('UserRemoved', userId);
        });

        this.addEvent('OnConnected', (connection: HubConnectionViewObject) => {
            bus.emit('OnConnected', connection);
        });

        this.addEvent('OnConnectionUpdate', (connection: HubConnectionViewObject) => {
            bus.emit('OnConnectionUpdate', connection);
        });

        this.addEvent('OnDisconnected', (userId: string, connectionId: string) => {
            bus.emit('OnDisconnected', userId, connectionId);
        });
    }

    public async toggleIsTyping(chatId: string | null | undefined, members: string[]): Promise<void> {
        await this.connection.send('ToggleIsTyping', chatId, members);
    }

    public async registerGuestChat(chatId: string): Promise<void> {
        await this.connection.invoke('RegisterGuestChat', chatId);
    }

    public async unregisterGuestChat(chatId: string): Promise<void> {
        await this.connection.invoke('UnregisterGuestChat', chatId);
    }
}

export default new ServerHub();
